import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  CardContent,
  Input,
  RegionDateInput,
  Tooltip,
  Field,
  FieldLabel,
  FieldDescription,
  FieldError,
  FieldGroup,
  Modal,
  Drawer,
  Switch,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui';
import { useAuthStore } from '../../../../store/auth';
import { useCurrentAcademy } from '../../../../hooks/useDataStore';
import { Term, hasAcademyManagementRole } from '@court-sync/shared';
import { termService } from '../../../../services/term';
import { useSubscriptionCheck } from '../../../../hooks/useSubscriptionCheck';
import { useFormatDate } from '../../../../hooks/useFormatDate';
import { useLessons, useTerms } from '../../../../hooks/useDataStore';
import {
  AlertTriangle,
  BookOpen,
  Calendar,
  Clock,
  Copy,
  Download,
  Upload,
  Search,
  MoreVertical,
  Plus,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { TermLessons } from './TermLessons';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import { ImportModal, ImportRow, type ImportResult } from '../../../../components/ImportModal';
import { downloadCSV } from '../../../../utils/csvUtils';
import { TERM_MANAGEMENT_TOUR_STEP_IDS } from '@/lib/tour-constants';
import type { OnboardingEmbeddedPageProps } from '@/components/onboarding/onboardingEmbeddedPage';
import { TermsGuideOverview } from './TermsGuideInfo';
import { DrawerTourHelp } from '@/components/tour/DrawerTourHelp';
import { PageFeatureHelp } from '@/components/tour/PageFeatureHelp';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { isPageTourComplete } from '@/lib/tours/pageTourCompletion';
import { scheduleAutoPageTour } from '@/lib/tours/scheduleAutoPageTour';
import { usePageTourReady } from '@/store/pageTourReady';

// Form validation schema
const termSchema = z.object({
  name: z.string().min(1, 'Term name is required').max(100, 'Name too long'),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().min(1, 'End date is required'),
  registrationDeadline: z.string().optional(),
  isActive: z.boolean(),
  copyLessonsFromTerm: z.boolean().optional(),
  sourceTermId: z.string().optional(),
}).refine((data) => {
  const startDate = new Date(data.startDate);
  const endDate = new Date(data.endDate);
  return endDate > startDate;
}, {
  message: 'End date must be after start date',
  path: ['endDate'],
}).refine((data) => {
  // If copyLessonsFromTerm is true, sourceTermId must be provided
  if (data.copyLessonsFromTerm && !data.sourceTermId) {
    return false;
  }
  return true;
}, {
  message: 'Please select a term to copy lessons from',
  path: ['sourceTermId'],
});

type TermFormData = z.infer<typeof termSchema>;

type TermListTab = 'current' | 'previous';

function TermFormSectionHeading({ title, description }: { title: string; description?: string }) {
  return (
    <div className="border-t border-border pt-5 first:border-t-0 first:pt-0">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {description ? <p className="mt-1 text-xs text-muted-foreground">{description}</p> : null}
    </div>
  );
}

function formatTermDurationLabel(startDate: Date, endDate: Date): string {
  const diffTime = endDate.getTime() - startDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const totalDays = diffDays === 0 ? 1 : diffDays;
  const weeks = Math.floor(totalDays / 7);
  const days = totalDays % 7;

  const parts: string[] = [];
  if (weeks > 0) {
    parts.push(`${weeks} week${weeks > 1 ? 's' : ''}`);
  }
  if (days > 0) {
    parts.push(`${days} day${days > 1 ? 's' : ''}`);
  }
  if (parts.length === 0) {
    return '1 day';
  }
  return parts.join(' ');
}

function TermManagementContent({ embedded = false }: OnboardingEmbeddedPageProps) {
  const { user } = useAuthStore();
  const { currentAcademy } = useCurrentAcademy();
  const navigate = useNavigate();
  const { hasValidSubscription, tooltipMessage } = useSubscriptionCheck();
  const { formatDate } = useFormatDate();
  // Select raw terms array (stable reference); derive academy list in useMemo to avoid
  // getSnapshot returning a new array every render and causing infinite re-renders.
  const {
    terms,
    isLoading: isLoadingTerms,
    createTerm: createTermInStore,
    updateTerm: updateTermInStore,
    deleteTerm: deleteTermFromStore,
  } = useTerms(currentAcademy?.id);
  const { refresh: refreshLessons } = useLessons(currentAcademy?.id);
  const [submitting, setSubmitting] = useState(false);
  const [viewingTerm, setViewingTerm] = useState<Term | null>(null);
  const [editingTerm, setEditingTerm] = useState<Term | null>(null);
  const [showForm, setShowForm] = useState(false);
  const TERM_FORM_ID = 'term-form';
  const [selectedActiveFilter, setSelectedActiveFilter] = useState<string>('all');
  const [termListTab, setTermListTab] = useState<TermListTab>('current');
  const [termSearchQuery, setTermSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [termToDelete, setTermToDelete] = useState<Term | null>(null);
  const [deleting, setDeleting] = useState(false);
  const itemsPerPage = 8;
  const [showImportModal, setShowImportModal] = useState(false);
  const termCreateTourScheduledRef = useRef(false);
  const cancelTermCreateTourScheduleRef = useRef<(() => void) | null>(null);
  const { startPageTour } = usePageTourActions();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<TermFormData>({
    resolver: zodResolver(termSchema),
    defaultValues: {
      isActive: true,
      copyLessonsFromTerm: false,
      sourceTermId: '',
      registrationDeadline: undefined,
    },
  });

  const copyLessonsFromTerm = watch('copyLessonsFromTerm');
  const startDateValue = watch('startDate');
  const endDateValue = watch('endDate');
  const isActiveValue = watch('isActive');

  const durationLabel = useMemo(() => {
    if (!startDateValue || !endDateValue) return null;
    return formatTermDurationLabel(new Date(startDateValue), new Date(endDateValue));
  }, [startDateValue, endDateValue]);


  // Check if user has academy owner access
  useEffect(() => {
    if (!user || !hasAcademyManagementRole(user)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const onSubmit = async (data: TermFormData) => {
    if (!currentAcademy) return;

    try {
      setSubmitting(true);

      // Transform form data to match Term type
      const termData = {
        academyId: currentAcademy.id,
        name: data.name,
        startDate: new Date(data.startDate),
        endDate: new Date(data.endDate),
        registrationDeadline: data.registrationDeadline ? new Date(data.registrationDeadline) : undefined,
        isActive: data.isActive,
      };

      if (editingTerm) {
        await updateTermInStore(editingTerm.id, termData);
        toast.success('Term updated successfully');
        handleCancelEdit();
      } else {
        const createdTerm = await createTermInStore(termData);
        toast.success('Term created successfully');

        // Copy lessons if requested
        if (data.copyLessonsFromTerm && data.sourceTermId && createdTerm?.id) {
          try {
            const copyResponse = await termService.copyLessonsFromTerm(
              createdTerm.id,
              data.sourceTermId
            );

            if (copyResponse.success && copyResponse.data) {
              const { copiedCount, errors: copyErrors } = copyResponse.data;
              if (copiedCount > 0) {
                toast.success(
                  `Successfully copied ${copiedCount} lesson${copiedCount !== 1 ? 's' : ''} from previous term`,
                  { duration: 4000 }
                );
              }
              if (copyErrors && copyErrors.length > 0) {
                console.warn('Some lessons failed to copy:', copyErrors);
                toast.error(
                  `${copyErrors.length} lesson${copyErrors.length !== 1 ? 's' : ''} failed to copy. Check console for details.`,
                  { duration: 5000 }
                );
              }
              // Refresh lessons so copied lessons appear immediately without browser refresh
              await refreshLessons();
            }
          } catch (copyError: any) {
            console.error('Error copying lessons:', copyError);
            toast.error(`Term created but failed to copy lessons: ${copyError.message || 'Unknown error'}`);
          }
        }

        if (createdTerm) {
          reset();
          handleView(createdTerm);
        } else {
          handleCancelEdit();
        }
      }
    } catch (error: any) {
      console.error('Error saving term:', error);
      toast.error(error.message || 'Failed to save term');
    } finally {
      setSubmitting(false);
    }
  };

  const handleView = (term: Term) => {
    setViewingTerm(term);
    setShowForm(false);
    setEditingTerm(null);
  };

  const handleEdit = (term: Term) => {
    setEditingTerm(term);
    setViewingTerm(null);
    setValue('name', term.name);
    setValue('startDate', new Date(term.startDate).toISOString().split('T')[0]);
    setValue('endDate', new Date(term.endDate).toISOString().split('T')[0]);
    setValue('registrationDeadline', term.registrationDeadline ? new Date(term.registrationDeadline).toISOString().split('T')[0] : '');
    setValue('isActive', term.isActive);
    setValue('copyLessonsFromTerm', false);
    setValue('sourceTermId', '');
    setShowForm(true);
  };

  const handleCancelEdit = () => {
    setEditingTerm(null);
    setShowForm(false);
    setViewingTerm(null);
    reset();
  };

  const handleDeleteClick = (term: Term) => {
    setTermToDelete(term);
  };

  const handleDeleteConfirm = async () => {
    if (!termToDelete) return;

    try {
      setDeleting(true);
      await deleteTermFromStore(termToDelete.id);
      toast.success('Term deleted successfully');
      setTermToDelete(null);
    } catch (error: any) {
      console.error('Error deleting term:', error);
      toast.error(error.message || 'Failed to delete term');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (!deleting) {
      setTermToDelete(null);
    }
  };


  const getTermStatus = (term: Term) => {
    const now = new Date();
    const startDate = new Date(term.startDate);
    const endDate = new Date(term.endDate);

    if (now < startDate) return 'upcoming';
    if (now >= startDate && now <= endDate) return 'active';
    return 'completed';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'upcoming':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'completed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  /** Calendar window (vs term.isActive). Distinct label so UI does not show two identical "Active" badges. */
  const getTermLifecycleLabel = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'Upcoming';
      case 'active':
        return 'In progress';
      case 'completed':
        return 'Completed';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const calculateTermDuration = (startDate: Date, endDate: Date) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Calculate the difference in milliseconds
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Handle same day case
    if (diffDays === 0) {
      return { weeks: 0, days: 1, totalDays: 1 };
    }

    // Calculate weeks and remaining days
    const weeks = Math.floor(diffDays / 7);
    const days = diffDays % 7;

    return { weeks, days, totalDays: diffDays };
  };


  const termsForTab = useMemo(() => {
    if (termListTab === 'previous') {
      return terms.filter((t) => getTermStatus(t) === 'completed');
    }
    return terms.filter((t) => getTermStatus(t) !== 'completed');
  }, [terms, termListTab]);

  const currentAndUpcomingCount = useMemo(
    () => terms.filter((t) => getTermStatus(t) !== 'completed').length,
    [terms]
  );
  const previousTermsCount = useMemo(
    () => terms.filter((t) => getTermStatus(t) === 'completed').length,
    [terms]
  );

  const filteredTerms = useMemo(() => {
    let list = termsForTab;
    if (selectedActiveFilter !== 'all') {
      const isActive = selectedActiveFilter === 'active';
      list = list.filter((t) => t.isActive === isActive);
    }
    const q = termSearchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((t) => (t.name || '').toLowerCase().includes(q));
    }
    return list;
  }, [termsForTab, selectedActiveFilter, termSearchQuery]);

  const sortedTerms = useMemo(() => {
    const sorted = [...filteredTerms];
    sorted.sort((a, b) => {
      if (termListTab === 'previous') {
        return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
      }
      const statusOrder = { upcoming: 0, active: 1, completed: 2 };
      const aStatus = getTermStatus(a);
      const bStatus = getTermStatus(b);
      const aOrder = statusOrder[aStatus as keyof typeof statusOrder] ?? 3;
      const bOrder = statusOrder[bStatus as keyof typeof statusOrder] ?? 3;
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    });
    return sorted;
  }, [filteredTerms, termListTab]);

  const hasActiveFilters =
    selectedActiveFilter !== 'all' || termSearchQuery.trim() !== '';

  const clearFilters = () => {
    setSelectedActiveFilter('all');
    setTermSearchQuery('');
  };

  const totalPages = Math.max(1, Math.ceil(sortedTerms.length / itemsPerPage));
  const paginatedTerms = sortedTerms.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [termListTab, termSearchQuery, selectedActiveFilter]);

  useEffect(() => {
    setCurrentPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const availableSourceTerms = editingTerm
    ? terms.filter((term) => term.id !== editingTerm.id)
    : terms;

  const handleExportTerms = () => {
    const exportData = terms.map(term => ({
      name: term.name,
      startDate: new Date(term.startDate).toISOString().split('T')[0],
      endDate: new Date(term.endDate).toISOString().split('T')[0],
      registrationDeadline: term.registrationDeadline ? new Date(term.registrationDeadline).toISOString().split('T')[0] : '',
      isActive: term.isActive,
    }));
    downloadCSV(`terms-${currentAcademy?.name?.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`, exportData);
    toast.success(`Exported ${terms.length} term${terms.length !== 1 ? 's' : ''}`);
  };

  const handleImportTerms = async (rows: ImportRow[]): Promise<ImportResult | undefined> => {
    if (!currentAcademy) {
      return undefined;
    }

    let createdCount = 0;
    let failedCount = 0;

    for (const row of rows) {
      if (!row.isValid || !row.data.name || !row.data.startDate || !row.data.endDate) {
        failedCount++;
        continue;
      }

      try {
        await createTermInStore({
          academyId: currentAcademy.id,
          name: row.data.name,
          startDate: new Date(row.data.startDate),
          endDate: new Date(row.data.endDate),
          registrationDeadline: row.data.registrationDeadline ? new Date(row.data.registrationDeadline) : undefined,
          isActive: row.data.isActive === 'true',
        });
        createdCount++;
      } catch (error: any) {
        console.error(`Failed to create term at row ${row.rowNumber}:`, error);
        failedCount++;
      }
    }

    toast.success(`Created ${createdCount} term${createdCount !== 1 ? 's' : ''}${failedCount > 0 ? `, ${failedCount} failed` : ''}`);
    return { created: createdCount, failed: failedCount };
  };

  usePageTourReady(
    'academy-terms-create',
    showForm && !editingTerm && Boolean(currentAcademy)
  );

  const openNewTermForm = useCallback(() => {
    reset({
      isActive: true,
      copyLessonsFromTerm: false,
      sourceTermId: '',
      registrationDeadline: undefined,
    });
    setShowForm(true);
  }, [reset]);

  useEffect(() => {
    if (!showForm) {
      termCreateTourScheduledRef.current = false;
      cancelTermCreateTourScheduleRef.current?.();
      cancelTermCreateTourScheduleRef.current = null;
    }
  }, [showForm]);

  const handleTermCreateDrawerPanelOpen = useCallback(() => {
    if (
      editingTerm ||
      termCreateTourScheduledRef.current ||
      isPageTourComplete(user?.preferences, 'academy-terms-create')
    ) {
      return;
    }
    termCreateTourScheduledRef.current = true;
    cancelTermCreateTourScheduleRef.current?.();
    cancelTermCreateTourScheduleRef.current = scheduleAutoPageTour(
      startPageTour,
      'academy-terms-create',
      user?.preferences
    );
  }, [editingTerm, startPageTour, user?.preferences]);

  const termsFormatGuide = (
    <div className="space-y-3 text-xs">
      <div>
        <p className="font-semibold mb-2">Required Columns:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">name</code> - Term name (e.g., "Spring 2025")</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">startDate</code> - YYYY-MM-DD format</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">endDate</code> - YYYY-MM-DD format</li>
        </ul>
      </div>
      <div>
        <p className="font-semibold mb-2">Optional Columns:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">registrationDeadline</code> - YYYY-MM-DD format</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">isActive</code> - "true" or "false"</li>
        </ul>
      </div>
      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded text-gray-700 dark:text-gray-300">
        <strong>Example:</strong> name,startDate,endDate,registrationDeadline,isActive<br/>
        Spring 2025,2025-03-01,2025-05-31,2025-02-15,true
      </div>
    </div>
  );

  if (!user || !hasAcademyManagementRole(user) || !currentAcademy) {
    return null;
  }

  return (
    <div className={embedded ? 'min-h-0' : 'min-h-screen'}>
      <div className="contents">
        {!embedded ? (
          <PageHeader
            title="Manage Academic Terms"
            subtitle="Create and manage terms for your academy"
            help={
              <PageFeatureHelp title="Academic terms">
                <TermsGuideOverview />
              </PageFeatureHelp>
            }
            helpAnchorId={TERM_MANAGEMENT_TOUR_STEP_IDS.GUIDE}
            actions={
              !showForm && !viewingTerm && !isLoadingTerms ? (
                <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                  <Button
                    id={TERM_MANAGEMENT_TOUR_STEP_IDS.NEW_TERM}
                    type="button"
                    size="sm"
                    onClick={() => openNewTermForm()}
                    disabled={!hasValidSubscription}
                  >
                    <Plus aria-hidden />
                    New Term
                  </Button>
                </Tooltip>
              ) : undefined
            }
          />
        ) : null}

        <Drawer
          isOpen={showForm}
          onClose={handleCancelEdit}
          panelId={!editingTerm ? TERM_MANAGEMENT_TOUR_STEP_IDS.FORM_DRAWER : undefined}
          onPanelOpen={!editingTerm ? handleTermCreateDrawerPanelOpen : undefined}
          title={editingTerm ? 'Edit term' : 'Create term'}
          description={
            editingTerm
              ? 'Update dates, registration settings, and visibility for this term.'
              : 'Set the term window, optional registration deadline, and copy lessons from a previous term.'
          }
          headerActions={
            !editingTerm ? (
              <DrawerTourHelp
                tourKey="academy-terms-create"
                suggestion="New to terms? Take a quick tour of this form before you save."
                buttonLabel="Tour this form"
              />
            ) : undefined
          }
          width="lg"
          footer={
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                <Button
                  id={TERM_MANAGEMENT_TOUR_STEP_IDS.FORM_SUBMIT}
                  type="submit"
                  form={TERM_FORM_ID}
                  disabled={submitting || !hasValidSubscription}
                >
                  {submitting ? 'Saving...' : editingTerm ? 'Update Term' : 'Create Term'}
                </Button>
              </Tooltip>
            </div>
          }
        >
              <form id={TERM_FORM_ID} onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <FieldGroup>
                  <div id={TERM_MANAGEMENT_TOUR_STEP_IDS.FORM_DETAILS}>
                  <TermFormSectionHeading
                    title="Term details"
                    description="Name this term and control whether it is available for scheduling."
                  />

                  <Field data-invalid={!!errors.name?.message}>
                    <FieldLabel htmlFor="term-name">Term name</FieldLabel>
                    <Input
                      id="term-name"
                      aria-invalid={!!errors.name?.message}
                      {...register('name')}
                      placeholder="e.g. Spring 2026, Term 3"
                    />
                    <FieldError>{errors.name?.message}</FieldError>
                  </Field>

                  <div className="mt-3 flex items-center justify-between gap-4 rounded-lg border border-border bg-muted/20 px-4 py-3">
                    <div className="min-w-0">
                      <FieldLabel htmlFor="term-is-active" className="mb-0">
                        Active term
                      </FieldLabel>
                      <FieldDescription className="mt-0.5">
                        {isActiveValue ? 'This term is marked active.' : 'This term is marked inactive.'}
                      </FieldDescription>
                    </div>
                    <Controller
                      name="isActive"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          id="term-is-active"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          aria-label={field.value ? 'Term is active' : 'Term is inactive'}
                        />
                      )}
                    />
                  </div>
                  </div>

                  <div id={TERM_MANAGEMENT_TOUR_STEP_IDS.FORM_SCHEDULE}>
                  <TermFormSectionHeading
                    title="Schedule"
                    description="Set when this term runs. Duration is calculated automatically."
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <Field data-invalid={!!errors.startDate?.message}>
                      <FieldLabel htmlFor="term-start-date">Start date</FieldLabel>
                      <Controller
                        name="startDate"
                        control={control}
                        render={({ field }) => (
                          <RegionDateInput
                            id="term-start-date"
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            region={currentAcademy?.region}
                            error={!!errors.startDate}
                          />
                        )}
                      />
                      <FieldError>{errors.startDate?.message}</FieldError>
                    </Field>

                    <Field data-invalid={!!errors.endDate?.message}>
                      <FieldLabel htmlFor="term-end-date">End date</FieldLabel>
                      <Controller
                        name="endDate"
                        control={control}
                        render={({ field }) => (
                          <RegionDateInput
                            id="term-end-date"
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            region={currentAcademy?.region}
                            error={!!errors.endDate}
                          />
                        )}
                      />
                      <FieldError>{errors.endDate?.message}</FieldError>
                    </Field>
                  </div>

                  <div
                    className={`mt-3 flex items-center gap-3 rounded-lg border px-4 py-3 ${
                      durationLabel
                        ? 'border-emerald-200 bg-emerald-50/80 dark:border-emerald-900/40 dark:bg-emerald-950/20'
                        : 'border-dashed border-border bg-muted/10'
                    }`}
                  >
                    <div
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                        durationLabel
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {durationLabel ? (
                        <Clock className="h-4 w-4" aria-hidden />
                      ) : (
                        <Calendar className="h-4 w-4" aria-hidden />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Duration
                      </p>
                      <p className="text-sm font-medium text-foreground">
                        {durationLabel ?? 'Set start and end dates to see duration'}
                      </p>
                    </div>
                  </div>
                  </div>

                  <TermFormSectionHeading
                    title="Registration"
                    description="Optionally close public enrolment before the term starts."
                  />

                  <Field data-invalid={!!errors.registrationDeadline?.message}>
                    <FieldLabel htmlFor="term-registration-deadline">Registration deadline</FieldLabel>
                    <Controller
                      name="registrationDeadline"
                      control={control}
                      render={({ field }) => (
                        <RegionDateInput
                          id="term-registration-deadline"
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          region={currentAcademy?.region}
                          error={!!errors.registrationDeadline}
                        />
                      )}
                    />
                    <FieldDescription>Leave empty to keep registration open for the full term.</FieldDescription>
                    <FieldError>{errors.registrationDeadline?.message}</FieldError>
                  </Field>

                  {!editingTerm ? (
                    <div id={TERM_MANAGEMENT_TOUR_STEP_IDS.FORM_COPY}>
                      <TermFormSectionHeading
                        title="Copy lessons"
                        description="Roll group and clinic lessons forward from an earlier term."
                      />

                      <div className="mt-3 rounded-lg border border-dashed border-primary/30 bg-primary/5 p-4">
                        <div className="flex items-start gap-3">
                          <Controller
                            name="copyLessonsFromTerm"
                            control={control}
                            render={({ field }) => (
                              <Checkbox
                                id="copyLessonsFromTerm"
                                checked={field.value ?? false}
                                onCheckedChange={(checked) => field.onChange(checked === true)}
                                className="mt-0.5"
                              />
                            )}
                          />
                          <div className="min-w-0 flex-1 space-y-1">
                            <label
                              htmlFor="copyLessonsFromTerm"
                              className="flex cursor-pointer items-center gap-2 text-sm font-medium text-foreground"
                            >
                              <Copy className="h-4 w-4 text-primary" aria-hidden />
                              Copy lessons from a previous term
                            </label>
                            <FieldDescription>
                              Saves time when rolling into the next season — lesson times and settings carry over.
                            </FieldDescription>
                          </div>
                        </div>

                        {copyLessonsFromTerm ? (
                          <div className="mt-4 border-t border-primary/20 pt-4">
                            <Field data-invalid={!!errors.sourceTermId?.message}>
                              <FieldLabel htmlFor="term-source-term">Copy from</FieldLabel>
                              <Controller
                                name="sourceTermId"
                                control={control}
                                render={({ field }) => (
                                  <Select value={field.value || undefined} onValueChange={field.onChange}>
                                    <SelectTrigger id="term-source-term">
                                      <SelectValue placeholder="Select a term" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {availableSourceTerms.map((term) => (
                                        <SelectItem key={term.id} value={term.id}>
                                          {term.name} ({formatDate(term.startDate)} – {formatDate(term.endDate)})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              />
                              {availableSourceTerms.length === 0 ? (
                                <FieldDescription>No previous terms are available to copy from yet.</FieldDescription>
                              ) : null}
                              <FieldError>{errors.sourceTermId?.message}</FieldError>
                            </Field>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </FieldGroup>
              </form>
        </Drawer>

        {/* Term Details View */}
        {viewingTerm && !showForm && (
          <div className="space-y-4 sm:space-y-6">
            <div className="flex flex-col gap-4 sm:gap-6">
              <div
                id={TERM_MANAGEMENT_TOUR_STEP_IDS.MANAGE_HEADER}
                className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl md:text-2xl break-words">
                    {viewingTerm.name}
                  </h3>
                  <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400 sm:mt-1 sm:text-sm">
                    {currentAcademy.name}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                    <Button
                      size="sm"
                      onClick={() => handleEdit(viewingTerm)}
                      disabled={!hasValidSubscription}
                    >
                      Edit Term
                    </Button>
                  </Tooltip>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setViewingTerm(null)}
                  >
                    Back to List
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-6">
                  {/* Status Badges */}
                  <div className="lg:col-span-3 flex flex-wrap items-center gap-1.5 sm:gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium sm:px-3 sm:py-1.5 sm:text-sm ${getStatusColor(getTermStatus(viewingTerm))}`}>
                      {getTermLifecycleLabel(getTermStatus(viewingTerm))}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium sm:px-3 sm:py-1.5 sm:text-sm ${viewingTerm.isActive
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                      }`}>
                      {viewingTerm.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>

                  {/* Start Date */}
                  <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:p-4">
                    <div className="mb-0.5 text-xs text-gray-600 dark:text-gray-400 sm:mb-1 sm:text-sm">Start Date</div>
                    <div className="text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
                      {formatDate(viewingTerm.startDate)}
                    </div>
                  </div>

                  {/* End Date */}
                  <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:p-4">
                    <div className="mb-0.5 text-xs text-gray-600 dark:text-gray-400 sm:mb-1 sm:text-sm">End Date</div>
                    <div className="text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
                      {formatDate(viewingTerm.endDate)}
                    </div>
                  </div>

                  {/* Duration */}
                  <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:p-4">
                    <div className="mb-0.5 text-xs text-gray-600 dark:text-gray-400 sm:mb-1 sm:text-sm">Duration</div>
                    <div className="text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
                      {(() => {
                        const duration = calculateTermDuration(viewingTerm.startDate, viewingTerm.endDate);
                        return (
                          <>
                            {duration.weeks > 0 && `${duration.weeks} week${duration.weeks > 1 ? 's' : ''}`}
                            {duration.weeks > 0 && duration.days > 0 && ' '}
                            {duration.days > 0 && `${duration.days} day${duration.days > 1 ? 's' : ''}`}
                            {duration.weeks === 0 && duration.days === 1 && '1 day'}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Registration Deadline */}
                  {viewingTerm.registrationDeadline && (
                    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:p-4">
                      <div className="mb-0.5 text-xs text-gray-600 dark:text-gray-400 sm:mb-1 sm:text-sm">Registration Deadline</div>
                      <div className="text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
                        {formatDate(viewingTerm.registrationDeadline)}
                      </div>
                    </div>
                  )}

                  {/* Max Capacity */}
                  {viewingTerm.maxCapacity && (
                    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:p-4">
                      <div className="mb-0.5 text-xs text-gray-600 dark:text-gray-400 sm:mb-1 sm:text-sm">Max Capacity</div>
                      <div className="text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
                        {viewingTerm.maxCapacity}
                      </div>
                    </div>
                  )}
                </div>
              </div>

            {/* Term Lessons */}
            <TermLessons term={viewingTerm} />
          </div>
        )}

        {/* Terms list — stay visible behind create/edit drawer */}
        {!viewingTerm && (
          isLoadingTerms ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : terms.length === 0 ? (
            <DashboardEmptyStateCard contentClassName="text-center sm:px-12">
              <DashboardEmptyStateIcon icon={BookOpen} />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No terms yet
              </h3>
              <TermsGuideOverview className="mx-auto max-w-2xl text-left" />
              {!showForm ? (
                <div className="mt-6">
                  <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                    <Button onClick={() => openNewTermForm()} disabled={!hasValidSubscription}>
                      Create your first term
                    </Button>
                  </Tooltip>
                </div>
              ) : null}
            </DashboardEmptyStateCard>
          ) : (
            <div className="space-y-5">
              <div id={TERM_MANAGEMENT_TOUR_STEP_IDS.LIST_TABS}>
              <DashboardTabNav<TermListTab>
                withMargin={false}
                tabs={[
                  {
                    id: 'current' as const,
                    label: 'Current & upcoming',
                    mobileLabel: 'Upcoming',
                    suffix: `(${currentAndUpcomingCount})`,
                  },
                  {
                    id: 'previous' as const,
                    label: 'Previous terms',
                    mobileLabel: 'Past',
                    suffix: `(${previousTermsCount})`,
                  },
                ]}
                activeTab={termListTab}
                onChange={setTermListTab}
                ariaLabel="Term list filters"
              />
              </div>

              <div
                id={TERM_MANAGEMENT_TOUR_STEP_IDS.FILTERS}
                className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
              >
                <div className="relative w-full min-w-0 sm:max-w-md">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    aria-hidden
                  />
                  <Input
                    type="search"
                    value={termSearchQuery}
                    onChange={(e) => setTermSearchQuery(e.target.value)}
                    placeholder="Search by name…"
                    className="h-9 w-full pl-9"
                    aria-label="Search terms"
                  />
                </div>
                <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto sm:shrink-0">
                  <div className="flex items-center gap-2">
                    <label htmlFor="term-status-filter" className="sr-only">
                      Record status
                    </label>
                    <select
                      id="term-status-filter"
                      value={selectedActiveFilter}
                      onChange={(e) => setSelectedActiveFilter(e.target.value)}
                      className="input h-9 min-h-9 w-[9rem]"
                    >
                      <option value="all">All statuses</option>
                      <option value="active">Active only</option>
                      <option value="inactive">Inactive only</option>
                    </select>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-9 w-9 shrink-0"
                          aria-label="Term list actions"
                        >
                          <MoreVertical className="h-4 w-4" aria-hidden />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[10rem]">
                        <DropdownMenuItem onClick={handleExportTerms}>
                          <Download className="h-4 w-4 mr-2" aria-hidden />
                          Export
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setShowImportModal(true)}>
                          <Upload className="h-4 w-4 mr-2" aria-hidden />
                          Import
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  {hasActiveFilters && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 shrink-0 whitespace-nowrap px-2.5"
                      onClick={clearFilters}
                    >
                      <span className="sm:hidden">Clear</span>
                      <span className="hidden sm:inline">Clear filters</span>
                    </Button>
                  )}
                </div>
              </div>

              {sortedTerms.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-muted/20 px-6 py-14 text-center">
                  <p className="text-gray-600 dark:text-gray-400">
                    {termsForTab.length === 0
                      ? termListTab === 'previous'
                        ? 'No previous terms yet. Past terms appear here after they end.'
                        : 'No current or upcoming terms. Create a term or check the Previous tab.'
                      : 'No terms match your filters.'}
                  </p>
                  {termsForTab.length > 0 && filteredTerms.length === 0 && (
                    <Button type="button" variant="outline" className="mt-4" onClick={clearFilters}>
                      Clear filters
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {paginatedTerms.map((term) => {
                      const status = getTermStatus(term);
                      return (
                        <Card
                          key={term.id}
                          className="relative flex flex-col h-full min-h-[20rem] hover:shadow-md transition-shadow"
                        >
                          <CardContent className="flex flex-col p-6 flex-1">
                            <div className="flex items-start justify-between gap-3 mb-4">
                              <div className="min-w-0 flex-1">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-2 leading-snug">
                                  {term.name}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                  {currentAcademy.name}
                                </p>
                              </div>
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(status)}`}>
                                  {getTermLifecycleLabel(status)}
                                </span>
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                                    term.isActive
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                  }`}
                                >
                                  {term.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>

                            <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-4 flex-1">
                              <div className="flex justify-between gap-2">
                                <span>Start</span>
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  {formatDate(term.startDate)}
                                </span>
                              </div>
                              <div className="flex justify-between gap-2">
                                <span>End</span>
                                <span className="font-medium text-gray-900 dark:text-gray-100">
                                  {formatDate(term.endDate)}
                                </span>
                              </div>
                              {(() => {
                                const duration = calculateTermDuration(term.startDate, term.endDate);
                                return (
                                  <div className="flex justify-between gap-2">
                                    <span>Duration</span>
                                    <span className="font-medium text-gray-900 dark:text-gray-100">
                                      {duration.weeks > 0 && `${duration.weeks} week${duration.weeks > 1 ? 's' : ''}`}
                                      {duration.weeks > 0 && duration.days > 0 && ' '}
                                      {duration.days > 0 && `${duration.days} day${duration.days > 1 ? 's' : ''}`}
                                      {duration.weeks === 0 && duration.days === 1 && '1 day'}
                                    </span>
                                  </div>
                                );
                              })()}
                              {term.registrationDeadline && (
                                <div className="flex justify-between gap-2">
                                  <span>Registration</span>
                                  <span className="font-medium text-gray-900 dark:text-gray-100">
                                    {formatDate(term.registrationDeadline)}
                                  </span>
                                </div>
                              )}
                              {term.maxCapacity != null && term.maxCapacity > 0 && (
                                <div className="flex justify-between gap-2">
                                  <span>Max capacity</span>
                                  <span className="font-medium text-gray-900 dark:text-gray-100">{term.maxCapacity}</span>
                                </div>
                              )}
                            </div>

                            <div className="flex flex-wrap gap-2 pt-4 mt-auto border-t border-gray-200 dark:border-gray-700">
                              <Button size="sm" variant="outline" onClick={() => handleView(term)}>
                                View
                              </Button>
                              <Tooltip content={tooltipMessage} disabled={hasValidSubscription}>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDeleteClick(term)}
                                  disabled={!hasValidSubscription}
                                  className="text-red-600 hover:text-red-700 hover:border-red-300"
                                >
                                  Delete
                                </Button>
                              </Tooltip>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                  {totalPages > 1 && (
                    <div className="flex flex-col gap-3 border-t border-gray-200 pt-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Showing {(currentPage - 1) * itemsPerPage + 1}–
                        {Math.min(currentPage * itemsPerPage, sortedTerms.length)} of {sortedTerms.length} terms
                      </p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage <= 1}
                        >
                          Previous
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage >= totalPages}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )
        )}
      </div>

      {/* Delete Term Confirmation Modal */}
      <Modal
        isOpen={!!termToDelete}
        onClose={handleDeleteCancel}
        title="Delete Term"
        maxWidth="sm"
      >
        <div className="space-y-4">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-lg flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                This action cannot be undone
              </p>
              <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                Deleting the term &quot;{termToDelete?.name}&quot; will permanently remove the term and all
                associated lessons, bookings, and attendance records.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={handleDeleteCancel} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleting} isLoading={deleting}>
              Delete Term
            </Button>
          </div>
        </div>
      </Modal>

      {/* Import Modal */}
      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportTerms}
        title="Import Terms"
        description="Upload a CSV file to import multiple terms at once"
        formatGuide={termsFormatGuide}
        importingLabel="Importing terms…"
        requiredColumns={['name', 'startDate', 'endDate']}
      />
    </div>
  );
}

export default function TermManagement(props: OnboardingEmbeddedPageProps = {}) {
  return <TermManagementContent {...props} />;
}
